// Custom unit editor: title, symbol, search terms, what it measures and its size.
// ➕ The conversion size is editable (Swift showed it read-only).
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { type DetailParams } from '../navigation';
import { useHeader } from '../hooks';
import { useLibrary } from '../store/library';
import { Caption, Chip, EmptyState, Field, Row, Screen, Section } from '../components/ui';
import type { CustomUnit } from '../types';

export default function UnitScreen({ route }: NativeStackScreenProps<DetailParams, 'Unit'>) {
  const { t } = useTranslation();
  const unit = useLibrary(s => s.units[route.params.id]);
  const put = useLibrary(s => s.put);
  useHeader({ title: unit?.title ?? '' }, [unit?.title]);
  if (!unit) return <EmptyState icon="unit" title={t('common.empty')} />;
  const update = (patch: Partial<CustomUnit>) => put('units', { ...unit, ...patch });
  // Stored in kg / L; edited in g / ml for readability.
  const sizeText = unit.toBase == null ? '' : String(+(unit.toBase * 1000).toPrecision(10));

  return (
    <Screen>
      <Field label={t('editors.title')} value={unit.title} onChangeText={v => update({ title: v })} />
      <Field label={t('editors.symbol')} value={unit.symbol} onChangeText={v => update({ symbol: v })} autoCapitalize="none" />
      <Section title={t('editors.dimension')}>
        <Row>
          <Chip label={t('editors.mass')} selected={unit.dimension === 'mass'} onPress={() => update({ dimension: 'mass' })} />
          <Chip label={t('editors.volume')} selected={unit.dimension === 'volume'} onPress={() => update({ dimension: 'volume' })} />
          <Chip label={t('editors.count')} selected={unit.dimension === null} onPress={() => update({ dimension: null, toBase: null })} />
        </Row>
        {unit.dimension ? (
          <Field
            label={t('editors.toBase')}
            value={sizeText}
            keyboardType="decimal-pad"
            onChangeText={v => {
              const n = Number(v.replace(',', '.'));
              update({ toBase: v.trim() && n > 0 ? n / 1000 : null });
            }}
          />
        ) : null}
      </Section>
      <Field
        label={t('editors.searchTerms')}
        value={unit.searchTerms.join('\n')}
        onChangeText={v => update({ searchTerms: v.split('\n').map(s => s.trim()).filter(Boolean) })}
        multiline
        style={{ minHeight: 80, textAlignVertical: 'top' }}
      />
      <Caption>{[unit.symbol, ...unit.searchTerms].filter(Boolean).join(', ')}</Caption>
    </Screen>
  );
}
